import React, { useCallback, useRef, useState } from 'react';
import { Lead } from '../types';
import LeadModal from '../components/leads/LeadModal';
import { leadsService } from '../services/supabase';
import { useToast } from '../context/ToastContext';
import { useQuota } from '../context/QuotaContext';
import { useI18n } from '../context/I18nContext';

export type RequestCreateLeadArgs = {
  prefillName: string;
  onCreated: (lead: Lead) => void;
};

/**
 * LeadSearch "Créer un lead" → LeadModal → select created lead.
 */
export function useCreateLeadFromSearch() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const { showQuotaLimit } = useQuota();
  const [isOpen, setIsOpen] = useState(false);
  const [defaultName, setDefaultName] = useState('');
  const onCreatedRef = useRef<((lead: Lead) => void) | null>(null);

  const requestCreateLead = useCallback(({ prefillName, onCreated }: RequestCreateLeadArgs) => {
    onCreatedRef.current = onCreated;
    setDefaultName(prefillName);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setDefaultName('');
    onCreatedRef.current = null;
  }, []);

  const finishCreate = useCallback(
    (lead: Lead, onCreated: ((lead: Lead) => void) | null) => {
      onCreatedRef.current = null;
      onCreated?.(lead);
      showToast(t('leads.created', { name: lead.name }) || t('common.save'), 'success');
      setIsOpen(false);
      setDefaultName('');
    },
    [showToast, t]
  );

  const handleSave = useCallback(
    async (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => {
      // Keep onCreatedRef until success so a failed save can be retried (closeAfterSave).
      const onCreated = onCreatedRef.current;

      const created = await leadsService.create(leadData);

      if (created && (created as any).error === 'QUOTA_LIMIT' && (created as any).quotaInfo) {
        showQuotaLimit((created as any).quotaInfo, {
          onDismiss: () => {
            // X / upgrade — cancel the create-from-search attempt
            handleClose();
          },
          onSlotFreed: async () => {
            const retried = await leadsService.create(leadData) as any;
            if (retried && !retried.error && retried.id) {
              finishCreate(retried as Lead, onCreated);
              return;
            }
            showToast(t('leads.createError') || t('common.error'), 'error');
          },
        });
        throw new Error('QUOTA_LIMIT');
      }

      if (!created || !(created as Lead).id) {
        showToast(t('leads.createError') || t('common.error'), 'error');
        throw new Error('CREATE_FAILED');
      }

      finishCreate(created as Lead, onCreated);
    },
    [finishCreate, handleClose, showQuotaLimit, showToast, t]
  );

  const leadCreateModal = (
    <LeadModal
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      defaultName={defaultName}
      closeAfterSave
    />
  );

  return { requestCreateLead, leadCreateModal };
}
